import { useEffect, useState, useCallback } from 'react'
import { useGameStore } from '../../store/gameStore'
import { ACHIEVEMENTS } from '../../engine/Achievements'
import Icon from './Icon'

/**
 * 勋章解锁 Toast - 监听 pendingUnlockIds, 弹出 4 秒自动消失
 * 一次只显示一个 (队列顺序), 显示完清空 store
 *
 * 关闭方式:
 * - 4 秒后自动淡出
 * - 点右上角 X 按钮立即关
 * - 点背景遮罩也关
 */
function AchievementToast() {
  const pendingIds = useGameStore(s => s.pendingUnlockIds)
  const consume = useGameStore(s => s.consumePendingUnlocks)
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [visible, setVisible] = useState(false)

  // 关闭当前 toast (供 X 按钮 + 背景点击共用)
  const close = useCallback(() => {
    setVisible(false)
    // 提前到淡出动画前清空 (动画 300ms, 立即清避免视觉残留)
    setCurrentId(null)
  }, [])

  // 弹出逻辑: pendingIds 非空时, 取第一个显示 4 秒
  useEffect(() => {
    if (currentId || pendingIds.length === 0) return

    // 取第一个待显示的
    const next = consume()[0]
    if (!next) return
    setCurrentId(next)
    setVisible(true)

    // 3.5 秒后开始淡出, 4 秒后清空
    const fadeTimer = setTimeout(() => setVisible(false), 3500)
    const clearTimer = setTimeout(() => setCurrentId(null), 4000)
    return () => {
      clearTimeout(fadeTimer)
      clearTimeout(clearTimer)
    }
  }, [pendingIds, currentId, consume])

  if (!currentId) return null
  const achievement = ACHIEVEMENTS.find(a => a.id === currentId)
  if (!achievement) return null

  return (
    // 外层背景: 点击关闭 (Toast 出现期间临时占用屏幕, 用户可主动 dismiss)
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ zIndex: 9999 }}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={`勋章解锁: ${achievement.name}`}
    >
      {/* 内层卡片: 阻止冒泡避免点 toast 文字时触发背景关闭 */}
      <div
        className={
          'pixel-box pixel-box-bright p-6 text-center transition-all duration-300 max-w-sm mx-4 relative overflow-hidden ' +
          (visible
            ? 'achievement-toast-anim'
            : 'opacity-0 scale-90')
        }
        style={{
          backgroundColor: 'var(--color-bg-base)',
          pointerEvents: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 右上角关闭按钮 */}
        <button
          onClick={close}
          aria-label="关闭勋章提示"
          className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center text-[color:var(--color-text-dim)] hover:text-[color:var(--color-bear)] transition-colors"
        >
          <Icon name="cross" size={12} />
        </button>

        <div className="font-pixel text-[10px] text-[color:var(--color-gold)] mb-2 tracking-wider inline-flex items-center gap-2">
          <Icon name="sparkle" size={12} />
          习得新策略
          <Icon name="sparkle" size={12} />
        </div>
        <div className="flex justify-center mb-3">
          <img
            src={achievement.imagePath}
            alt={achievement.name}
            width={96}
            height={96}
            className="object-contain"
            style={{ imageRendering: 'auto' }}
          />
        </div>
        <div className="font-pixel text-lg text-[color:var(--color-text-bright)] mb-2">
          {achievement.name}
        </div>
        <div className="font-pixel-body text-sm text-[color:var(--color-text-dim)] mb-3">
          {achievement.description}
        </div>
        <div className="font-pixel-display text-[9px] text-[color:var(--color-text-dim)]">
          ► 已收入勋章成就
        </div>
      </div>
    </div>
  )
}

export default AchievementToast
